import Phaser from "phaser";
import type { BigNumber } from "@yjsgames/bignumber";
import type { TextDirection } from "@yjsgames/i18n";
import { FONT_FAMILY, TYPO } from "./uiTokens.js";
import { PALETTE } from "../themes/palette.js";
import { formatBig } from "./formatNumber.js";
import { computeHudLayout } from "./hudLayout.js";
import { DEPTH } from "../objects/depthLayers.js";
import { createAccountWarningBadge } from "./accountWarningBadge.js";

export interface HudData {
  readonly gold: BigNumber;
  readonly diamond: BigNumber;
  readonly chapter: number;
  readonly bestChapter: number;
  /** 현재 월드 번호. 상단 월드 칩에는 단위 표기 없이 숫자만 표시한다. */
  readonly worldNo: number;
  /** 현재 월드 아이콘 텍스처 키. 월드 칩의 아이콘을 월드별로 교체한다. */
  readonly worldIconKey: string;
  /** 강화석 보유량 (DPS 칩 자리 — 강화석은 환생 유지 영구 자산이라 상시 표기). 소량 카운트 = number. */
  readonly upgradeStone: number;
  readonly baseDamage: BigNumber;
}

// ── 레이아웃 상수 ──────────────────────────────────────────────────
const BAR_H = 36;
const CHIP_H = 24;
const CHIP_PAD_X = 6;
const ICON_SIZE = 16;
const CHIP_GAP = 4;
const SETTINGS_SIZE = 30;
const SIDE_PAD = 6;
const CHIP_COUNT = 5;
const TEXT_STYLE: Phaser.Types.GameObjects.Text.TextStyle = {
  fontFamily: FONT_FAMILY,
  fontSize: TYPO.caption,
  color: PALETTE.hudText,
};
const CHIP_LABEL_MIN_FONT_SIZE = 9;

interface Chip {
  bg: Phaser.GameObjects.NineSlice;
  icon: Phaser.GameObjects.Image;
  label: Phaser.GameObjects.Text;
  labelMaxWidth: number;
  width: number;
  lastText: string;
}

/**
 * 상단 HUD — fantasy PNG 기반 NineSlice topbar + chip 5종 + 설정 기어 버튼.
 * depth 30~31로 게임뷰 위에 항상 표시.
 *
 * 좌표는 `computeHudLayout`(순수 함수)에 위임 — RTL이면 설정 버튼·chip 순서·chip 내부(아이콘/라벨)를
 * 좌우 미러링한다. LTR 값은 기존 하드코딩과 동일(회귀 0).
 */
export class HudView {
  private readonly goldChip: Chip;
  private readonly diamondChip: Chip;
  private readonly worldChip: Chip;
  private readonly stageChip: Chip;
  /** 강화석 칩 — 환생 유지 영구 자산이라 상단 HUD에 상시 표기한다. */
  private readonly stoneChip: Chip;
  // relayout(방향 전환)용 — settingsBtn/icon은 지역변수였으나 재배치 위해 멤버화 + 배치 상수 보관.
  private readonly settingsBtn: Phaser.GameObjects.NineSlice;
  private readonly settingsIcon: Phaser.GameObjects.Image;
  private readonly settingsAccountWarning: Phaser.GameObjects.Container;
  private readonly hudX: number;
  private readonly hudBarW: number;
  private readonly settingsBtnY: number;
  private readonly chipY: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    options?: { onSettingsClick?: () => void; dir?: TextDirection; accountNeedsLink?: boolean },
  ) {
    const w = scene.scale.width;
    const barW = w - x * 2;
    this.hudX = x;
    this.hudBarW = barW;
    const layout = computeHudLayout({
      dir: options?.dir ?? "ltr",
      x,
      barW,
      sidePad: SIDE_PAD,
      settingsSize: SETTINGS_SIZE,
      chipGap: CHIP_GAP,
      chipCount: CHIP_COUNT,
      chipPadX: CHIP_PAD_X,
      iconSize: ICON_SIZE,
    });

    // topbar 배경 (9-slice)
    scene.add
      .nineslice(x, y, "hud_topbar_bg", undefined, barW, BAR_H, 14, 14, 10, 10)
      .setName("hudTopbarBg")
      .setOrigin(0, 0)
      .setDepth(DEPTH.hudBar);

    // 설정 기어 버튼 (LTR=우측 끝 / RTL=좌측 끝)
    const settingsBtnX = layout.settingsBtnX;
    const settingsBtnY = y + BAR_H / 2;
    this.settingsBtnY = settingsBtnY;
    const settingsBtn = scene.add
      .nineslice(
        settingsBtnX,
        settingsBtnY,
        "hud_icon_button",
        undefined,
        SETTINGS_SIZE,
        SETTINGS_SIZE,
        10,
        10,
        10,
        10,
      )
      .setName("hudSettingsButton")
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH.hudContent)
      .setInteractive({ useHandCursor: true });
    this.settingsBtn = settingsBtn;
    this.settingsIcon = scene.add
      .image(settingsBtnX, settingsBtnY, "icon_settings")
      .setName("hudSettingsIcon")
      .setDisplaySize(16, 16)
      .setDepth(DEPTH.hudContent);
    this.settingsAccountWarning = createAccountWarningBadge(scene, settingsBtnX + 10, settingsBtnY - 10, {
      depth: DEPTH.hudContent + 1,
    }).setVisible(options?.accountNeedsLink ?? false);
    if (options?.onSettingsClick) {
      settingsBtn.on("pointerup", options.onSettingsClick);
    }

    const chipY = y + (BAR_H - CHIP_H) / 2;
    this.chipY = chipY;

    // chip 좌표·내부 배치는 layout(순수 함수)에서. idx는 논리 순서(0=gold).
    const makeChip = (idx: number, iconKey: string): Chip => {
      const cx = layout.chipXs[idx] ?? 0;
      const bg = scene.add
        .nineslice(cx, chipY, "hud_currency_chip", undefined, layout.chipW, CHIP_H, 10, 10, 9, 9)
        .setName("hudChipBg")
        .setOrigin(0, 0)
        .setDepth(DEPTH.hudContent);
      const icon = scene.add
        .image(cx + layout.inner.iconX, chipY + CHIP_H / 2, iconKey)
        .setName("hudChipIcon")
        .setDisplaySize(ICON_SIZE, ICON_SIZE)
        .setDepth(DEPTH.hudContent);
      const label = scene.add
        .text(cx + layout.inner.labelX, chipY + CHIP_H / 2, "", TEXT_STYLE)
        .setName("hudChipLabel")
        .setOrigin(layout.inner.labelOriginX, 0.5)
        .setDepth(DEPTH.hudContent);
      const labelMaxWidth = Math.max(12, layout.chipW - CHIP_PAD_X * 2 - ICON_SIZE - 4);
      return { bg, icon, label, labelMaxWidth, width: layout.chipW, lastText: "" };
    };

    this.goldChip = makeChip(0, "icon_gold");
    this.stoneChip = makeChip(1, "icon_upgrade_stone"); // 강화석 칩 (전용 6각 원석 아이콘)
    this.diamondChip = makeChip(2, "icon_diamond");
    this.worldChip = makeChip(3, "icon_world_circuit");
    this.stageChip = makeChip(4, "icon_stage");
  }

  update(d: HudData): void {
    fitChipLabel(this.goldChip, formatBig(d.gold));
    fitChipLabel(this.diamondChip, formatBig(d.diamond));
    if (this.worldChip.icon.texture.key !== d.worldIconKey) {
      this.worldChip.icon.setTexture(d.worldIconKey);
    }
    fitChipLabel(this.worldChip, String(d.worldNo));
    fitChipLabel(this.stageChip, `${d.chapter}/${d.bestChapter}`);
    fitChipLabel(this.stoneChip, String(d.upgradeStone));
  }

  setAccountNeedsLink(value: boolean): void {
    this.settingsAccountWarning.setVisible(value);
  }

  /** 방향 전환(RTL) — computeHudLayout 재호출로 설정버튼·chip 좌표/내부 미러 재배치(destroy 0; topbar는 dir 무관 불변). */
  relayout(dir: TextDirection): void {
    const layout = computeHudLayout({
      dir,
      x: this.hudX,
      barW: this.hudBarW,
      sidePad: SIDE_PAD,
      settingsSize: SETTINGS_SIZE,
      chipGap: CHIP_GAP,
      chipCount: CHIP_COUNT,
      chipPadX: CHIP_PAD_X,
      iconSize: ICON_SIZE,
    });
    this.settingsBtn.setPosition(layout.settingsBtnX, this.settingsBtnY);
    this.settingsIcon.setPosition(layout.settingsBtnX, this.settingsBtnY);
    this.settingsAccountWarning.setPosition(layout.settingsBtnX + 10, this.settingsBtnY - 10);
    const chips = [this.goldChip, this.stoneChip, this.diamondChip, this.worldChip, this.stageChip];
    chips.forEach((chip, idx) => {
      const cx = layout.chipXs[idx] ?? 0;
      chip.bg.setPosition(cx, this.chipY);
      chip.icon.setPosition(cx + layout.inner.iconX, this.chipY + CHIP_H / 2);
      chip.label
        .setPosition(cx + layout.inner.labelX, this.chipY + CHIP_H / 2)
        .setOrigin(layout.inner.labelOriginX, 0.5);
    });
  }

  /** 골드 칩 아이콘 월드 좌표 (보상 fly-to 타겟). */
  get goldChipPos(): { x: number; y: number } {
    return { x: this.goldChip.icon.x, y: this.goldChip.icon.y };
  }

  /** 다이아 칩 아이콘 월드 좌표 (보상 fly-to 타겟). */
  get diamondChipPos(): { x: number; y: number } {
    return { x: this.diamondChip.icon.x, y: this.diamondChip.icon.y };
  }

  /** 챕터 칩 월드 사각형. 가이드 하이라이트가 챕터 진행 위치를 직접 가리킨다. */
  get chapterChipRect(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.stageChip.bg.x,
      y: this.stageChip.bg.y,
      width: this.stageChip.width,
      height: CHIP_H,
    };
  }
}

function fitChipLabel(chip: Chip, text: string): void {
  if (chip.lastText === text) return;
  chip.lastText = text;
  const captionSize = Number.parseInt(TYPO.caption, 10);
  chip.label.setText(text).setScale(1).setFontSize(captionSize);
  for (let fontSize = captionSize; chip.label.width > chip.labelMaxWidth && fontSize > CHIP_LABEL_MIN_FONT_SIZE; ) {
    fontSize--;
    chip.label.setFontSize(fontSize);
  }
  if (chip.label.width > chip.labelMaxWidth) {
    chip.label.setScale(chip.labelMaxWidth / chip.label.width);
  }
}
